// src/pages/RegisterPage.jsx
import React, { useMemo, useState, useRef } from "react";
import {track} from '../../analytics/service';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { peekRedirect, withRedirect } from "../../utils/authRedirect";
import "../css/AuthPages.css";

import {
  validateEmail,
  validateCAP,
  validateProvincia,
  validateCodiceFiscale,
} from "../../utils/validators";

import CityField from "../../components/CityField";
import AuthSplitLayout from "../../components/auth/AuthSplitLayout";
import RegistrationMarketingChoices,{useRegistrationMarketing} from '../../components/auth/RegistrationMarketingChoices';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function getPwdStrength(pwd = "") {
  let score = 0;
  if (pwd.length >= 6) score++;
  if (pwd.length >= 10) score++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  score = Math.min(4, score);

  const labels = ["Debole", "Ok", "Buona", "Forte", "Ottima"];
  const colors = ["var(--danger)", "#f59e0b", "#10b981", "#0ea5e9", "#0d6efd"];

  return {
    score,
    label: labels[score],
    color: colors[score],
    percent: (score / 4) * 100,
  };
}

export default function RegisterPage() {
  const analyticsStarted = useRef(false);
  const marketing=useRegistrationMarketing();
  const navigate = useNavigate();
  const location = useLocation();
  // pagina richiesta prima del login: va nel link di verifica email e nel
  // "Vai al login" finale (query, oppure copia locale)
  const postLoginRedirect = peekRedirect(location.search);

  const getPrefillRef = () => {
    try {
      const urlRef = new URLSearchParams(window.location.search).get("ref");
      const lsRef = localStorage.getItem("pendingReferralCode");
      return (urlRef || lsRef || "").trim();
    } catch {
      return "";
    }
  };

  const [form, setForm] = useState({
    name: "",
    last_name: "",
    email: "",
    password: "",
    phone: "",
    address: "",
    cap: "",
    citta: "",
    provincia: "",
    codice_fiscale: "",
    referral_code: getPrefillRef(),
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // hard confirm: devi scegliere dall’elenco
  const [cityConfirmed, setCityConfirmed] = useState(false);

  const validateField = (name, value) => {
    switch (name) {
      case "email":
        return validateEmail(value) ? "" : "Email non valida";
      case "cap":
        return validateCAP(value) ? "" : "CAP deve avere 5 cifre";
      case "provincia":
        return validateProvincia(value) ? "" : "Provincia: 2 lettere (es. MO)";
      case "codice_fiscale":
        return validateCodiceFiscale(value) ? "" : "Codice fiscale non valido";
      case "password":
        return value && value.length >= 6 ? "" : "Minimo 6 caratteri";
      case "name":
        return value ? "" : "Nome obbligatorio";
      case "last_name":
        return value ? "" : "Cognome obbligatorio";
      case "citta":
        return value ? "" : "Città obbligatoria";
      default:
        return "";
    }
  };

  const setField = (name, value, { markTouched = false } = {}) => {
    setForm((f) => ({ ...f, [name]: value }));

    if (markTouched) {
      setTouched((t) => ({ ...t, [name]: true }));
    }

    // valida se già touched o se sto forzando touched
    if (touched[name] || markTouched) {
      setErrors((p) => ({ ...p, [name]: validateField(name, value) }));
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const onCapChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 5);
    setField("cap", v);
  };

  const onBlur = (e) => {
    const { name, value } = e.target || {};
    if (!name) return;
    setField(name, value, { markTouched: true });
  };

  const mustFields = useMemo(
    () => [
      "name",
      "last_name",
      "email",
      "password",
      "citta",
      "provincia",
      "cap",
      "codice_fiscale",
    ],
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError("");
    setShowSuccess(false);

    // valida obbligatori
    const nextErr = Object.fromEntries(
      mustFields.map((k) => [k, validateField(k, form[k])])
    );

    setTouched((t) => ({
      ...t,
      ...Object.fromEntries(mustFields.map((k) => [k, true])),
    }));
    setErrors((p) => ({ ...p, ...nextErr }));

    if (Object.values(nextErr).some(Boolean)) return;

    // hard confirm città
    if (!cityConfirmed) {
      setErrors((prev) => ({
        ...prev,
        citta: "Seleziona una città dall'elenco",
      }));
      setTouched((prev) => ({ ...prev, citta: true }));
      return;
    }

    const payload = {
      name: form.name,
      last_name: form.last_name,
      email: form.email,
      password: form.password,
      phone: form.phone || undefined,
      address: form.address || undefined,
      cap: form.cap || undefined,
      citta: form.citta || undefined,
      provincia: form.provincia || undefined,
      codice_fiscale: form.codice_fiscale || undefined,
      role: "user",
      referral_code: form.referral_code?.trim() || undefined,
      redirect: postLoginRedirect || undefined,
      marketing: marketing.payload,
    };

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        track('registration_result',{outcome:'failure',error_code:'request_rejected',account_type:'customer'});
        setError(data?.error || "Errore nella registrazione");
        return;
      }

      setShowSuccess(true);
      track('registration_result',{outcome:'success',account_type:'customer'});

      try {
        localStorage.removeItem("pendingReferralCode");
      } catch {}
    } catch {
      track('registration_result',{outcome:'failure',error_code:'network',account_type:'customer'});
      setError("Errore di rete");
    } finally {
      setLoading(false);
    }
  };

  const strength = getPwdStrength(form.password);

  return (
    <>
      <AuthSplitLayout
        heroTitle="Più tempo per te. Al resto pensiamo noi."
        heroSubtitle="Crea il tuo account e prenota il primo ritiro in pochi minuti."
        cardTitle="Crea il tuo account"
        cardSubtitle="Inizia a usare StirUp in pochi minuti."
        showLoginLink
      >
        <form onSubmit={handleSubmit} noValidate onChangeCapture={event=>{
          if (!analyticsStarted.current && event.target.value && !['checkbox','file'].includes(event.target.type)) {
            analyticsStarted.current=true;track('registration_started',{account_type:'customer'});
          }
        }}>
          {error && (
            <div className="auth-error" role="alert">
              {error}
            </div>
          )}

          <div className="form-grid two">
            <div className="field">
              <label htmlFor="name">Nome</label>
              <input
                id="name"
                name="name"
                value={form.name}
                onChange={onChange}
                onBlur={onBlur}
                required
                className={errors.name && touched.name ? "input-invalid" : ""}
              />
              {errors.name && touched.name && (
                <div className="error-text">{errors.name}</div>
              )}
            </div>

            <div className="field">
              <label htmlFor="last_name">Cognome</label>
              <input
                id="last_name"
                name="last_name"
                value={form.last_name}
                onChange={onChange}
                onBlur={onBlur}
                required
                className={
                  errors.last_name && touched.last_name ? "input-invalid" : ""
                }
              />
              {errors.last_name && touched.last_name && (
                <div className="error-text">{errors.last_name}</div>
              )}
            </div>
          </div>

          <div className="form-grid two">
            <div className="field">
              <label htmlFor="phone">Telefono</label>
              <input
                id="phone"
                name="phone"
                value={form.phone}
                onChange={onChange}
                onBlur={onBlur}
                autoComplete="tel"
                inputMode="tel"
              />
            </div>

            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                onBlur={onBlur}
                required
                className={
                  errors.email && touched.email ? "input-invalid" : ""
                }
              />
              {errors.email && touched.email && (
                <div className="error-text">{errors.email}</div>
              )}
            </div>
          </div>

          <div className="field">
            <label htmlFor="password">Password</label>
            <div className="pwd-wrap">
              <input
                id="password"
                type={showPwd ? "text" : "password"}
                name="password"
                value={form.password}
                onChange={onChange}
                onBlur={onBlur}
                required
                placeholder="Almeno 6 caratteri"
                className={
                  errors.password && touched.password ? "input-invalid" : ""
                }
              />
              <button
                type="button"
                className="pwd-toggle"
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? "Nascondi password" : "Mostra password"}
              >
                {showPwd ? "🙈" : "👁️"}
              </button>
            </div>

            <div className="pwd-meter" aria-hidden>
              <div
                className="pwd-meter-bar"
                style={{
                  width: `${strength.percent}%`,
                  background: strength.color,
                }}
              />
            </div>

            <div className="pwd-meter-label">
              Sicurezza: <strong>{strength.label}</strong>
            </div>

            {errors.password && touched.password && (
              <div className="error-text">{errors.password}</div>
            )}
          </div>

          <div className="field">
            <label htmlFor="address">Indirizzo</label>
            <input
              id="address"
              name="address"
              value={form.address}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Via e civico"
            />
          </div>

          <div className="field field-with-suggestions">
            <CityField
              label="Città"
              value={form.citta}
              provinciaValue={form.provincia}
              capValue={form.cap}
              hardConfirm
              confirmed={cityConfirmed}
              setConfirmed={setCityConfirmed}
              error={errors.citta && touched.citta ? errors.citta : ""}
              resetProvinciaOnType={true}
              resetCapOnType={true}
              onChangeCity={(v, meta) => {
                // meta arriva dal CityField: decide se resettare provincia/cap
                setForm((f) => ({
                  ...f,
                  citta: v,
                  provincia: meta?.resetProvincia ? "" : f.provincia,
                  cap: meta?.resetCap ? "" : f.cap,
                }));

                setCityConfirmed(false);

                // valida live se già toccato
                if (touched.citta) {
                  setErrors((p) => ({
                    ...p,
                    citta: validateField("citta", v),
                  }));
                }
              }}
              onApplyCity={({ name, provincia, cap }) => {
                setForm((f) => ({
                  ...f,
                  citta: name || "",
                  provincia: (provincia || "").toUpperCase(),
                  cap: cap || "",
                }));

                setCityConfirmed(true);

                setTouched((t) => ({
                  ...t,
                  citta: true,
                  provincia: true,
                  cap: true,
                }));

                setErrors((e) => ({
                  ...e,
                  citta: "",
                  provincia: "",
                  cap: "",
                }));
              }}
              inputClassName={
                errors.citta && touched.citta ? "input-invalid" : ""
              }
              showEmptyHint
            />
          </div>

          <div className="form-grid two">
            <div className="field">
              <label htmlFor="provincia">Provincia</label>
              <input
                id="provincia"
                name="provincia"
                value={form.provincia}
                readOnly
                placeholder="es. MO"
                className={
                  errors.provincia && touched.provincia ? "input-invalid" : ""
                }
              />
              {errors.provincia && touched.provincia && (
                <div className="error-text">{errors.provincia}</div>
              )}
            </div>

            <div className="field">
              <label htmlFor="cap">CAP</label>
              <input
                id="cap"
                name="cap"
                value={form.cap}
                onChange={onCapChange}
                onBlur={onBlur}
                inputMode="numeric"
                className={errors.cap && touched.cap ? "input-invalid" : ""}
              />
              {errors.cap && touched.cap && (
                <div className="error-text">{errors.cap}</div>
              )}
            </div>
          </div>

          <div className="field">
            <label htmlFor="codice_fiscale">Codice Fiscale</label>
            <input
              id="codice_fiscale"
              name="codice_fiscale"
              value={form.codice_fiscale}
              onChange={onChange}
              onBlur={onBlur}
              className={
                errors.codice_fiscale && touched.codice_fiscale
                  ? "input-invalid"
                  : ""
              }
            />
            {errors.codice_fiscale && touched.codice_fiscale && (
              <div className="error-text">{errors.codice_fiscale}</div>
            )}
          </div>

          <div className="field">
            <label htmlFor="referral_code">Codice amico (opzionale)</label>
            <input
              id="referral_code"
              name="referral_code"
              value={form.referral_code}
              onChange={onChange}
              placeholder="es. REF-AB12CD"
              autoComplete="off"
            />
            <div className="muted" style={{ marginTop: 4 }}>
              Se sei arrivato dal link di un amico, è già compilato.
            </div>
          </div>

          <RegistrationMarketingChoices value={marketing} disabled={loading}/>
          <div className="reg-actions">
            <button
              type="submit"
              className="reg-submit-btn"
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? "Invio…" : "Registrati"}
            </button>
          </div>

          <div className="reg-bottom-links">
            <p className="muted">
              Sei un fornitore?{" "}
              <Link
                className="auth-link"
                to={withRedirect("/supplier-register", postLoginRedirect)}
              >
                Registra il tuo profilo →
              </Link>
            </p>
          </div>
        </form>
      </AuthSplitLayout>

      {showSuccess && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-card">
            <div className="modal-icon">✅</div>
            <h3>Registrazione completata!</h3>
            <p>
              Grazie per esserti registrato. Riceverai a breve una email per
              confermare il tuo account.
            </p>
            <button
              className="reg-submit-btn"
              onClick={() => navigate(withRedirect("/login", postLoginRedirect))}
            >
              Vai al login
            </button>
          </div>
        </div>
      )}
    </>
  );
}
